<template>
	<section
		style="display: flex; flex-direction: column; margin-bottom: 20px; gap: 20px"
	>
		<div class="recherche">
			<!-- Champ de recherche -->
			<VCombobox
				v-model="selectedCity"
				:items="cities"
				item-text="nameWithTruncatedPostalCodes"
				:search-input.sync="search"
				:filter="(item) => item"
				autofocus
				hint="Recherche par nom ou code postal"
				label="Rechercher une ville"
				style="max-width: 400px; min-width: 300px"
			/>

			<!-- Filtres -->
			<div
				class="d-flex flex-column flex-sm-row"
				style="gap: 20px"
			>
				<!-- Filtre pour les enfants -->
				<VSwitch
					v-model="childrenFilter"
				>
					<template #label>
						<div :class="childrenFilter ? 'am-blue--text' : ''">
							Pour&nbsp;les&nbsp;enfants
						</div>
					</template>
				</VSwitch>

				<!-- Filtre pour les séances à distance -->
				<VSwitch
					v-model="teleconsultationFilter"
				>
					<template #label>
						<div :class="teleconsultationFilter ? 'am-blue--text' : ''">
							Séances&nbsp;à&nbsp;distance
						</div>
					</template>
				</VSwitch>
			</div>
		</div>

		<!-- Bouton de recherche -->
		<VBtn
			:loading="isLoading"
			color="primary"
			style="width: fit-content; align-self: center;"
			@click="searchPsychologists"
		>
			Rechercher
		</VBtn>
	</section>
</template>

<script lang="ts">
	import Vue from 'vue';
	import Component from 'vue-class-component';
	import { VBtn, VCombobox, VIcon, VSwitch } from 'vuetify/lib/components';
	import { debounce } from '@cnamts/vue-dot/src/functions/debounce';
	import { PsychologistApiService } from '@/services/api/psychologist-api-service';
	import { AddressApiService } from '@/services/api/address-api-service';
	import { City } from '@/types/models/City';
	import { SEARCH_DELAY_MILLISECONDS } from '@/constants';
	import { Watch } from 'vue-property-decorator';
	import { ApiLog } from '@/services/decorators/log-service';
	import { eventCode } from '@/constants/trace-constant';

	/**
	 * Recherche de psychologues
	 */
	@Component({
		components: {
			VCombobox,
			VBtn,
			VIcon,
			VSwitch
		}
	})
	export default class SearchSection extends Vue {
		// Filtre pour les enfants
		childrenFilter = false;

		// Filtre pour les séances à distance
		teleconsultationFilter = false;

		// Chargement des psychologues
		isLoading = false;

		// Liste des villes
		cities: City[] = [];

		// Ville sélectionnée
		selectedCity: City | null = null;

		// Champ de recherche de la ville
		search = '';

		// Débouncer pour la recherche de psychologues
		debounceSearch = debounce(this.searchCities, SEARCH_DELAY_MILLISECONDS);

		/**
		 * Déclenché lorsque la ville sélectionnée change
		 */
		@Watch('search')
		onSearchChange(): void {
			this.debounceSearch();
		}

		/**
		 * Récupère la liste des psychologues suivant la recherche faite
		 */
		@ApiLog(eventCode.RECHERCHE)
		searchPsychologists(): void {
			const city = this.cities.find(c => c.code === this.selectedCity?.code);
			if (city) {
				this.isLoading = true;
				this.$emit('on-change-city', city);
				PsychologistApiService.getPsychologistListByCoordinatesAndChildrenAndTeleconsultation(
					city.coordinates_x,
					city.coordinates_y,
					this.childrenFilter,
					this.teleconsultationFilter
				)
					.then((psychologistList) => this.$emit('on-change-psychologist-list', psychologistList))
					.finally(() => {
						this.isLoading = false;
						this.$emit('is-search-done', true);
					});
			}
		}

		/**
		 * Récupère les villes dont le nom ou le code postal correspond à la recherche faite
		 */
		searchCities(): void {
			// Vérifie si le champ de recherche contient déjà une ville de la liste
			const foundCity = this.cities.find(c => c.nameWithTruncatedPostalCodes === this.search);

			// Si la ville n'est pas dans la liste, on la recherche
			if (!foundCity && this.search && this.search !== '') {
				AddressApiService.getCityListByNameOrDepartment(this.search.trim().toLowerCase()).then(c => {
					this.cities = c;
				});
			}
		}
	}
</script>

<style scoped>
.recherche {
	display: flex;
	gap: 2rem;
	justify-content: center;
}

/* Style appliqué sur les écrans dont la largeur est supérieure à 700px */
@media screen and (max-width: 700px) {
	.recherche {
		flex-direction: column;
		align-items: center;
	}
}
</style>
